import axios from "axios";
import {store} from '../store/Store';
import * as config from "./../configs/Config";
import {clearAuth} from "./Authentication";


const getAxiosInstance = async () => {
    const state = store.getState();
    return axios.create({
        baseURL: config.API_URL,
        headers: {
            'Authorization': 'Bearer ' + state.auth.jwtToken,
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        },
    });
};



export const getArtist = async (name, noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.get("/artists/" + encodeURIComponent(name))
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (err.response && err.response.status === 404) {
                    return null;
                }
                if (!noRetry) {
                    return refreshAndRetry(err, () => {
                        return getArtist(name, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};

export const getAlbum = async (albumId, noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.get("/albums/" + albumId)
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (err.response && err.response.status === 404) {
                    return null;
                }
                if (!noRetry) {
                    return refreshAndRetry(err, () => {
                        return getAlbum(albumId, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};

export const getHome = async (noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.get("/home")
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (!noRetry) {
                    return refreshAndRetry(err, () => {
                        return getHome(true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};

export const getHistory = async (noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.get("/history")
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (!noRetry) {
                    return refreshAndRetry(err, () => {
                        return getHistory(true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};

// Fire-and-forget: a lost play event is never worth surfacing to the user.
export const trackPlay = async (catalogId, intentional) => {
    try {
        const axiosInstance = await getAxiosInstance();
        axiosInstance.post("/plays", {catalog_id: catalogId, intentional: !!intentional}).catch(() => {});
    } catch (err) {
    }
};

export const search = async (lookup, noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();

        return await axiosInstance.get("/search/tracks?limit=10&q=" + lookup)
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return search(lookup, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};

export const getSongs = async (paginator, noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();
        let requestBody = {
            params: {
                page: paginator.meta.current_page,
                per_page: paginator.meta.per_page
            }
        }
        return await axiosInstance.get("/me", requestBody)
            .then((response) => {
                return response.data;
            }).catch(function (err) {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return getSongs(paginator, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};


export const addCatalog = async (catalogId, noRetry) => {
    try {
        let formData = new FormData();
        formData.append('catalog_id', catalogId);

        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.post("/catalogs", formData)
            .then((response) => {
                return response.data.data;
            }).catch(function (err) {
                if (!noRetry) {
                    return refreshAndRetry(err, () => {
                        return addCatalog(catalogId, true)
                    })
                        .catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        console.error(err);
    }
};


export const deleteSong = async (song, noRetry) => {
    try {
        let endpoint = null;
        if (song.type === 'song') {
            endpoint = "/songs/" + song.id;
        } else if (song.type === 'catalog') {
            endpoint = "/catalogs/" + song.id;
        } else {
            await errorHandler('Invalid song.type ' + song.type);
        }

        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.delete(endpoint)
            .then((response) => {
                if (response.status === 204) {
                    return Promise.resolve(true);
                } else {
                    errorHandler('Failed to remove song');
                    return Promise.resolve(false);
                }
            }).catch(function (err) {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return deleteSong(song, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        errorHandler(err)
    }
};

export const prepareS3Upload = async (file, noRetry) => {
    try {
        let formData = new FormData();
        formData.append('filename', file.name)
        formData.append('filetype', file.type)

        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.post('/upload/prepare', formData)
            .catch((err) => {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return prepareS3Upload(file, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
}

export const uploadToS3 = async (url, fields, file) => {
    try {
        const formData = new FormData();

        Object.entries({...fields, file}).forEach(([key, value]) => {
            formData.append(key, value);
        });
        return await fetch(url, {method: 'POST', body: formData})
            .catch((err) => {
                errorHandler(err);
            });
    } catch (err) {
        await errorHandler(err);
    }
}

export const storeSong = async (url, filename, noRetry) => {
    try {
        let formData = new FormData();
        formData.append('url', url);

        if (filename) {
            formData.append('filename', filename); //optional
        }

        const axiosInstance = await getAxiosInstance();
        return await axiosInstance.post('/songs', formData)
            .then((response) => {
                return response.data.data;
            }).catch(function (err) {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return storeSong(url, filename, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }

}


export const refreshToken = async () => {
    // Sanctum tokens are long-lived; 401 means revoked or missing → re-login.
    clearAuth()
    window.location = '/login'
}


export const fetchData = async (url, noRetry) => {
    try {
        const axiosInstance = await getAxiosInstance();

        return axiosInstance.get(url, {})
            .then(function (response) {
                return response;
            }).catch((err) => {
                if (!noRetry) {
                    refreshAndRetry(err, () => {
                        return fetchData(url, true)
                    }).catch((err) => errorHandler(err));
                } else {
                    errorHandler(err);
                }
            });
    } catch (err) {
        await errorHandler(err);
    }
};


export const refreshAndRetry = async (err, callback) => {
    if (err?.response?.status === 401) {
        clearAuth()
        window.location = '/login'
    }
}


export const errorHandler = async (err) => {
    try {
        console.error('errorHandler', err);
    } catch (err) {
        console.error(err);
    }
}


/** Apple catalog ids -> our analyzed songs (same order; unanalyzed ids absent). */
export const getSongsByApple = async (appleIds) => {
    if (!appleIds.length) {
        return [];
    }
    try {
        const axiosInstance = await getAxiosInstance();
        const res = await axiosInstance.get('/songs/by-apple?ids=' + appleIds.join(','));
        return res.data?.songs || [];
    } catch (err) {
        return [];
    }
};
